/**
 * Dias úteis — a MESMA conta para a API (numerador: dias do mês com acesso,
 * coletados do Clerk) e para a web (denominador do "12/21" da apresentação e
 * do inventário). Vivia duplicada em três lugares e só olhava o dia da semana:
 * feriado em dia de semana contava como dia útil sem acesso, e o mês parecia
 * ter mais dias de trabalho do que teve.
 *
 * Dia útil = segunda a sexta que NÃO está em `FERIADOS`. A lista é a dos
 * feriados nacionais mais os de Pernambuco e do Recife, onde fica a matriz
 * e o fuso de referência da coleta (America/Recife). Datas móveis (Carnaval,
 * Sexta-feira Santa, Corpus Christi) entram ano a ano — acrescentar o ano
 * seguinte é editar esta lista.
 *
 * As chaves são `YYYY-MM-DD` no fuso de Recife, o mesmo formato que a coleta
 * usa para agrupar sessões por dia.
 */
export const FERIADOS: ReadonlySet<string> = new Set([
  // ── 2026 ─────────────────────────────────────────────────────────────────
  '2026-01-01', // Confraternização Universal
  '2026-02-16', // Carnaval (segunda)
  '2026-02-17', // Carnaval (terça)
  '2026-03-06', // Revolução Pernambucana (PE)
  '2026-04-03', // Sexta-feira Santa
  '2026-04-21', // Tiradentes
  '2026-05-01', // Dia do Trabalho
  '2026-06-04', // Corpus Christi
  '2026-06-24', // São João (PE)
  '2026-07-16', // Nossa Senhora do Carmo (Recife)
  '2026-09-07', // Independência
  '2026-10-12', // Nossa Senhora Aparecida
  '2026-11-02', // Finados
  '2026-11-15', // Proclamação da República
  '2026-11-20', // Consciência Negra
  '2026-12-08', // Nossa Senhora da Conceição (Recife)
  '2026-12-25', // Natal
  // ── 2027 ─────────────────────────────────────────────────────────────────
  '2027-01-01',
  '2027-02-08', // Carnaval (segunda)
  '2027-02-09', // Carnaval (terça)
  '2027-03-06',
  '2027-03-26', // Sexta-feira Santa
  '2027-04-21',
  '2027-05-01',
  '2027-05-27', // Corpus Christi
  '2027-06-24',
  '2027-07-16',
  '2027-09-07',
  '2027-10-12',
  '2027-11-02',
  '2027-11-15',
  '2027-11-20',
  '2027-12-08',
  '2027-12-25',
]);

/** Dia da semana (0 = domingo) de uma chave `YYYY-MM-DD`, sem depender do fuso da máquina. */
function diaDaSemana(chave: string): number {
  return new Date(`${chave}T12:00:00Z`).getUTCDay();
}

/** `true` se a chave `YYYY-MM-DD` é dia útil: seg–sex e fora de `FERIADOS`. */
export function ehDiaUtil(chave: string): boolean {
  const dow = diaDaSemana(chave);
  return dow >= 1 && dow <= 5 && !FERIADOS.has(chave);
}

/**
 * Total de dias úteis FIXADO pela gestão para um mês (`YYYY-MM`), quando o
 * calendário da empresa difere do civil (recesso, ponte, parada). Vale só
 * para o DENOMINADOR do carimbo; o numerador continua contando os dias em que
 * houve acesso. Setembro/2026: informado como 20 (o civil daria 21).
 */
export const DIAS_UTEIS_FIXADOS: Readonly<Record<string, number>> = {
  '2026-09': 20,
};

/** Quantidade de dias úteis do mês-calendário (`mes` de 1 a 12). */
export function diasUteisDoMes(ano: number, mes: number): number {
  const fixado = DIAS_UTEIS_FIXADOS[`${ano}-${String(mes).padStart(2, '0')}`];
  if (fixado !== undefined) return fixado;
  // Dia 0 do mês seguinte = último dia deste mês.
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  let uteis = 0;
  for (let dia = 1; dia <= ultimo; dia++) {
    const chave = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    if (ehDiaUtil(chave)) uteis++;
  }
  return uteis;
}

/**
 * Dias úteis do mês em que cai um instante ISO, no fuso de Recife (UTC-3, sem
 * horário de verão). Um ISO de 01/03 01:30Z é 28/02 22:30 em Recife: o mês
 * certo é fevereiro. É o denominador do carimbo "NN/DD".
 */
export function diasUteisDoMesDe(iso: string): number {
  const chave = new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Recife' });
  return diasUteisDoMes(Number(chave.slice(0, 4)), Number(chave.slice(5, 7)));
}
